import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { adminApi, api, getStudentId, setStudentId } from "@/lib/api";
import { playClickSound } from "@/lib/sounds";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import Onboarding from "@/pages/Onboarding";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import LinkAccount from "@/pages/LinkAccount";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import CurriculumBrowser from "@/pages/CurriculumBrowser";
import TutorSession from "@/pages/TutorSession";
import Practice from "@/pages/Practice";
import Assessment from "@/pages/Assessment";
import Mastery from "@/pages/Mastery";
import ParentDashboard from "@/pages/ParentDashboard";
import TrialExpired from "@/pages/TrialExpired";
import ContactUs from "@/pages/ContactUs";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CreateStudySession from "@/pages/study/CreateStudySession";
import JoinStudySession from "@/pages/study/JoinStudySession";
import StudyLobby from "@/pages/study/StudyLobby";
import StudyLive from "@/pages/study/StudyLive";
import StudyComplete from "@/pages/study/StudyComplete";
import StudyHistory from "@/pages/study/StudyHistory";

const RequireStudent = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let active = true;
    api.get("/auth/me").then(({ data }) => {
      if (!active) return;
      setStudentId(data.id);
      setStatus(data.trial_active === false ? "expired" : "ok");
    }).catch(() => {
      if (!active) return;
      setStatus(getStudentId() ? "needs-link" : "none");
    });
    return () => { active = false; };
  }, []);

  if (status === "checking") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="require-student-loading">Loading...</div>;
  }
  if (status === "none") {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }
  if (status === "needs-link") {
    return <Navigate to="/secure-account" replace />;
  }
  if (status === "expired") {
    return <TrialExpired />;
  }
  return children;
};

const HomeGate = () => {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let active = true;
    api.get("/auth/me").then(({ data }) => {
      if (!active) return;
      setStudentId(data.id);
      setStatus(data.trial_active === false ? "expired" : "ok");
    }).catch(() => {
      if (!active) return;
      setStatus(getStudentId() ? "needs-link" : "none");
    });
    return () => { active = false; };
  }, []);

  if (status === "checking") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="home-gate-loading">Loading...</div>;
  }
  if (status === "none") return <Landing />;
  if (status === "needs-link") return <Navigate to="/secure-account" replace />;
  if (status === "expired") return <TrialExpired />;
  return <Home />;
};

const RequireAdmin = ({ children }) => {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let active = true;
    adminApi.get("/admin/me").then(() => {
      if (active) setStatus("ok");
    }).catch(() => {
      if (active) setStatus("denied");
    });
    return () => { active = false; };
  }, []);

  if (status === "checking") {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="require-admin-loading">Loading...</div>;
  }
  if (status === "denied") {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

function App() {
  useEffect(() => {
    const handler = (e) => {
      const btn = e.target.closest("button");
      if (btn && !btn.disabled) playClickSound();
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return (
    <div className="App font-body">
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/secure-account" element={<LinkAccount />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/" element={<HomeGate />} />
          <Route path="/curriculum" element={<RequireStudent><CurriculumBrowser /></RequireStudent>} />
          <Route path="/tutor/:mode" element={<RequireStudent><TutorSession /></RequireStudent>} />
          <Route path="/tutor/:mode/:conceptId" element={<RequireStudent><TutorSession /></RequireStudent>} />
          <Route path="/practice/:conceptId" element={<RequireStudent><Practice /></RequireStudent>} />
          <Route path="/assessment/:conceptId" element={<RequireStudent><Assessment /></RequireStudent>} />
          <Route path="/mastery" element={<RequireStudent><Mastery /></RequireStudent>} />
          <Route path="/parent" element={<RequireStudent><ParentDashboard /></RequireStudent>} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/study/create" element={<RequireStudent><CreateStudySession /></RequireStudent>} />
          <Route path="/study/join" element={<RequireStudent><JoinStudySession /></RequireStudent>} />
          <Route path="/study/lobby/:code" element={<RequireStudent><StudyLobby /></RequireStudent>} />
          <Route path="/study/live/:code" element={<RequireStudent><StudyLive /></RequireStudent>} />
          <Route path="/study/complete/:code" element={<RequireStudent><StudyComplete /></RequireStudent>} />
          <Route path="/study/history" element={<RequireStudent><StudyHistory /></RequireStudent>} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
